import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

export default function Layout() {
  const { t, i18n } = useTranslation('common');
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    document.documentElement.lang = i18n.language || 'zh-TW';
  }, [i18n.language]);

  const WHATSAPP_NUMBER = '85262322466';
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t('cta.whatsappMessage'))}`;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/20">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt={t('brand.alt')}
              className="w-9 h-9 rounded-full object-cover"
            />
            <span className="font-semibold text-[#0a4c6b] text-lg">{t('brand.name')}</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/properties" className="text-gray-700 hover:text-[#0a4c6b] transition text-sm font-medium">{t('nav.properties')}</Link>
            <Link to="/packages" className="text-gray-700 hover:text-[#0a4c6b] transition text-sm font-medium">{t('nav.packages')}</Link>
            <Link to="/experiences" className="text-gray-700 hover:text-[#0a4c6b] transition text-sm font-medium">{t('nav.experiences')}</Link>
            <Link to="/plan" className="text-black hover:text-[#0a4c6b] transition text-sm font-medium">{t('nav.plan')}</Link>
            <Link to="/guide" className="text-gray-700 hover:text-[#0a4c6b] transition text-sm font-medium">{t('nav.guide')}</Link>
            <LanguageSwitcher />
            <Link to="/plan" className="bg-[#0a4c6b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition">{t('nav.cta')}</Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenu ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenu && (
          <div className="md:hidden border-t border-gray-100 bg-white/95 backdrop-blur-lg px-4 py-4 space-y-3">
            <Link to="/properties" onClick={() => setMobileMenu(false)} className="block text-gray-700 py-2">{t('nav.properties')}</Link>
            <Link to="/packages" onClick={() => setMobileMenu(false)} className="block text-gray-700 py-2">{t('nav.packages')}</Link>
            <Link to="/experiences" onClick={() => setMobileMenu(false)} className="block text-gray-700 py-2">{t('nav.experiences')}</Link>
            <Link to="/plan" onClick={() => setMobileMenu(false)} className="block text-black py-2">{t('nav.plan')}</Link>
            <Link to="/guide" onClick={() => setMobileMenu(false)} className="block text-gray-700 py-2">{t('nav.guide')}</Link>
            <Link to="/plan" onClick={() => setMobileMenu(false)} className="block bg-[#0a4c6b] text-white px-4 py-2 rounded-lg text-center">{t('nav.cta')}</Link>
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0d1b2a] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('brand.name')}</h3>
              <p className="text-gray-400 text-sm">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('footer.quickLinks')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <Link to="/properties" className="block hover:text-white transition">{t('nav.properties')}</Link>
                <Link to="/packages" className="block hover:text-white transition">{t('nav.packages')}</Link>
                <Link to="/plan" className="block hover:text-white transition">{t('nav.plan')}</Link>
                <Link to="/guide" className="block hover:text-white transition">{t('nav.guide')}</Link>
                <Link to="/experiences" className="block hover:text-white transition">{t('nav.experiences')}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('footer.payment')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>{t('footer.paymeFps')}</p>
                <p>{t('footer.alipayWechat')}</p>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('footer.contact')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>{t('footer.whatsapp')}</p>
                <p>{t('footer.email')}</p>
                <p>{t('footer.hours')}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-500">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
        aria-label={t('accessibility.whatsappLabel')}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
